import React, { useEffect, useState } from "react";
import { connect, useSelector } from "react-redux";
import { Table, DatePicker } from 'antd';
import dayjs from "dayjs";
import Card from "components/card";
import CrudOption from "components/dropdown/crudoption";
import { useCRUDAccess } from "utils/useCRUDAccess";
import { DATE_FORMAT_MDY, MODULE_TIMECARDS, TIME_FORMAT_12HM } from "variables/constants";
import TimeCardForm from "./TimeCardForm";
import PopupButton from "components/button/PopupButton";
import { NumberFormatter, handleTableChange } from "utils";
import { getTimeCardList, doTimeCardDelete } from "../../../../redux/timecard/timeCardState";
import SearchFilter from "components/search/SearchFilter";

const TimeCardTable = ({doSearch, doDelete, recordList, filters, pagination, ...props}) => {
	const user = useSelector((state) => state.auth.user);
	const { RangePicker } = DatePicker;

	const [currentSearchValue, setCurrentSearchValue] = useState('');
	const [dateRangeKey, setDateRangeKey] = useState(Date.now());

	const checkCRUDAccess = useCRUDAccess();
	const accessRights = checkCRUDAccess(MODULE_TIMECARDS, ['view', 'edit', 'delete'], true);
	
	const dateRangeValue = filters?.start_date && filters?.end_date 
		? [dayjs(filters.start_date), dayjs(filters.end_date)]
		: null;

	const columns = [
		{
	        title: `Employee Name`,
	        dataIndex: 'tmc_employee_name',
	        key: 'tmc_employee_name',
	        width: '20%',
	        render:(tmc_employee_name) => <span>{tmc_employee_name}</span> 
	    },
		{
	        title: `Date`,
	        dataIndex: 'tmc_date',
	        key: 'tmc_date',
	        width: '10%',
	        render:(tmc_date) => <span>{dayjs(tmc_date).format(DATE_FORMAT_MDY)}</span> 
	    },
		{
	        title: `Day`,
	        dataIndex: 'tmc_date',
	        key: 'tmc_day',
	        width: '10%',
	        render:(tmc_date) => <span>{dayjs(tmc_date).format('dddd')}</span> 
	    },		
		{
	        title: `Shift Schedule`,
	        dataIndex: 'schedule',
	        key: 'schedule',
	        width: '18%',
	        render:(schedule, record) => <span>{(schedule?.esd_time_in ? `${dayjs(`${record.tmc_date} ${schedule?.esd_time_in}`).format(TIME_FORMAT_12HM)} - ${dayjs(`${record.tmc_date} ${schedule?.esd_time_out}`).format(TIME_FORMAT_12HM)}` : 'Rest Day')}</span>
	    },	
		{
	        title: `Punch In / Punch Out`,
	        dataIndex: 'tmc_punch_in',
	        key: 'tmc_punch_in',
	        width: '18%',
	        render:(tmc_punch_in, record) => <div>{tmc_punch_in && dayjs(tmc_punch_in).format(TIME_FORMAT_12HM)} - {record.tmc_punch_out && dayjs(record.tmc_punch_out).format(TIME_FORMAT_12HM)}</div>
	    },
		{
	        title: <div className="w-full text-right">Worked Hours</div>,
	        dataIndex: 'tmc_worked_hours',
	        key: 'tmc_worked_hours',
	        width: '13%',
	        render:(tmc_worked_hours) => <div className="pr-2 text-right">{tmc_worked_hours > 0 ? NumberFormatter(tmc_worked_hours, 2) : '-'}</div> 
	    },	    
	    {
	        title: <PopupButton id="add_time_card" title="Add Time Card" label="Add" btnClass="btn-small" moduleId={MODULE_TIMECARDS}>
						<TimeCardForm recordId="0" mode="edit"/>
					</PopupButton>,
	        dataIndex: 'tmc_id',
	        key: 'tmc_id',
	        width: '11%',
	        render:(tmc_id) => <CrudOption 
					baseRoute="/console/time-cards" 
					recordId={tmc_id}
					allowView={accessRights.view}
					allowEdit={accessRights.edit}
					allowDelete={accessRights.delete}
				/>
	    },
    ];

	useEffect(() => {
		doSearch({paginate: pagination.per_page});
	}, [doSearch, pagination.per_page]);

	const handleDateChange = (dates, dateStrings) => {
		const newFilters = {
			paginate: pagination.per_page,
			page: 1
		};

		if (dates && dates[0] && dates[1]) {
			newFilters.start_date = dateStrings[0];
			newFilters.end_date = dateStrings[1];
		} 

		if (currentSearchValue) {
			newFilters.keyword = currentSearchValue;
		}

		doSearch(newFilters);
	};

	const handleSearch = (searchFilters) => {
		const searchValue = searchFilters.keyword || '';
		setCurrentSearchValue(searchValue);
		
		setDateRangeKey(Date.now());
		
		const newFilters = {
			paginate: pagination.per_page,
			page: 1
		};
		
		if (searchValue) {
			if (filters?.start_date && filters?.end_date) {
				newFilters.start_date = filters.start_date;
				newFilters.end_date = filters.end_date;
			}
			newFilters.keyword = searchValue;
		} 
		
		doSearch(newFilters);
	};
	
  	return (
	    <Card extra={"w-full h-full p-4"}>
	      	<div className="h-full w-full overflow-x-auto">
	      	
				<div className="grid grid-cols-1 md:grid-cols-2 gap-2 items-center">
					{user?.user_level !== 5 && (
						<div className="p-1">
							<SearchFilter
								placeholder="Search by Employee Name"
								searchHandler={handleSearch}
								filters={filters}
								pagination={pagination}
							/>
						</div>
					)}

					<div className={`p-2 flex justify-end ${user?.user_level === 5 ? 'col-span-2' : 'col-span-1'}`}>
						<RangePicker
							key={dateRangeKey}
							className="border-gray-50 dark:border-gray-200 bg-gray-50 dark:bg-white/30
										focus-within:border-emerald-500 focus-within:ring-2 focus-within:ring-emerald-500/50 
										focus-within:outline-none h-9 w-full md:w-72 !bg-transparent !border !rounded-lg"
							format="YYYY-MM-DD"
							onChange={handleDateChange}
							placeholder={['Start Date', 'End Date']}
							allowClear
							value={dateRangeValue}
						/>
					</div>
				</div>

	      		<Table
		        	dataSource={recordList} 
		        	columns={columns} 
		            rowKey={(record) => record.tmc_id}
					pagination={{...pagination, showTotal: (total, range) => `${range[0]} - ${range[1]} of ${total} records`,}}    
					scroll={{ x: 'max-content' }}
					onChange={handleTableChange(doSearch, filters)}               	
	        	/>
	      	</div>
	    </Card>
  	);
};

const mapStateToProps = (state) => ({
	recordList: state.timeCard.timeCardList?.items,
	pagination: state.timeCard.timeCardList?.pagination,
	filters: state.timeCard.timeCardList?.filters,
	requesting: state.timeCard.timeCardList?.requesting,
	error: state.timeCard.timeCardList?.error,
});

const mapDispatchToProps = (dispatch) => ({
	doSearch: (filters) => dispatch(getTimeCardList(filters)),
	doDelete: (id) => dispatch(doTimeCardDelete(id)),
});

export default connect(mapStateToProps, mapDispatchToProps)(TimeCardTable);
